import { Pipe, PipeTransform } from '@angular/core';
import { HistoryItem } from '../models/history-item';
import { CommunicationType, CommunicationTypeMap, GovernanceCommitteeMap, HistoryAttributeKey, HistoryUpdateType } from '../models/enums';
import { capitalizeFirstLetter } from '../utils/utils';

@Pipe({
  name: 'historyDescription'
})
export class HistoryDescriptionPipe implements PipeTransform {

  transform(item: HistoryItem, entityKey: string): string {
    if (item.attributeKey === null) {
      switch (item.updateType) {
        case HistoryUpdateType.CREATE:
          return 'Entity created';
        case HistoryUpdateType.ARCHIVE:
          return 'Entity archived';
        case HistoryUpdateType.UNARCHIVE:
          return 'Entity unarchived';
      }
    }

    switch (entityKey) {
      case ('contact'):
        return this.handleContactFields(item);
      case ('organisation'):
        return this.handleOrganisationFields(item);
      case ('project'):
        return this.handleProjectFields(item);
      case ('event'):
        return this.handleEventFields(item);
      default:
        return "N/A"
    }
  }

  handleContactFields(item: HistoryItem) {
    switch (item.attributeKey) {
      case (HistoryAttributeKey.CONTACT_NAME):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Contact name updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown contact name action ${item.updateType}`
        }

      case (HistoryAttributeKey.CONTACT_AFFILIATION):
        switch (item.updateType) {
          case HistoryUpdateType.ADD:
            return `Affiliation added ${item.newValue !== null ? ': ' + item.newValue : ''}`;
          case HistoryUpdateType.UPDATE:
            return `Affiliation updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          case HistoryUpdateType.DELETE:
            return `Affiliation removed ${item.oldValue !== null ? ': ' + item.oldValue : ''}`;
          default:
            return `Unknown affiliation action ${item.updateType}`
        }

      case (HistoryAttributeKey.CONTACT_PROJECT):
        switch (item.updateType) {
          case HistoryUpdateType.ADD:
            return `Project(s) added: ${item.newValue}`;
          case HistoryUpdateType.DELETE:
            return `Project(s) removed: ${item.oldValue}`;
          default:
            return `Unknown project action ${item.updateType}`
        }

      case (HistoryAttributeKey.CONTACT_INITIATIVE):
        switch (item.updateType) {
          case HistoryUpdateType.ADD:
            return `Initiative(s) added: ${item.newValue}`;
          case HistoryUpdateType.DELETE:
            return `Initiative(s) removed: ${item.oldValue}`;
          default:
            return `Unknown initiative action ${item.updateType}`
        }

      case (HistoryAttributeKey.CONTACT_SCIENTIFIC_ADVISORY_BOARD):
      case (HistoryAttributeKey.CONTACT_GENERAL_ASSEMBLY):
      case (HistoryAttributeKey.CONTACT_BOARD_OF_DIRECTORS):
      case (HistoryAttributeKey.CONTACT_EXECUTIVE_COMMITTEE):
      case (HistoryAttributeKey.CONTACT_NOMINATION_COMMITTEE):
      case (HistoryAttributeKey.CONTACT_ETHICS_COMMITTEE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if (item.newValue?.toUpperCase() === 'FALSE')
              return `Contact left ${GovernanceCommitteeMap[item.attributeKey]}`;
            else if (item.newValue?.toUpperCase() === 'TRUE')
              return `Contact joined ${GovernanceCommitteeMap[item.attributeKey]}`;
            else
              return `Contact ${item.newValue} ${GovernanceCommitteeMap[item.attributeKey]}`;
          default:
            return `Unknown governance action ${item.updateType}`
        }

      case (HistoryAttributeKey.CONTACT_COMMUNICATION_TYPE_1):
      case (HistoryAttributeKey.CONTACT_COMMUNICATION_TYPE_2):
      case (HistoryAttributeKey.CONTACT_COMMUNICATION_TYPE_3):
      case (HistoryAttributeKey.CONTACT_COMMUNICATION_TYPE_4):
      case (HistoryAttributeKey.CONTACT_COMMUNICATION_TYPE_5):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if (item.newValue === 'NONE')
              return `Contact left ${CommunicationTypeMap[item.attributeKey]}`;
            else if (item.newValue === 'false')
              return `Contact opted out from ${CommunicationTypeMap[item.attributeKey]}`;
            else if (item.newValue === 'true')
              return `Contact opted in for  ${CommunicationTypeMap[item.attributeKey]}`;
            else
              return `Unknown communication action ${item.updateType} ${item.newValue}`
          default:
            return `Unknown communication action ${item.updateType}`
        }
      default:
        return `Unknown item ${item.attributeKey} ${item.updateType}`
    }
  }

  handleOrganisationFields(item: HistoryItem) {
    switch (item.attributeKey) {
      case (HistoryAttributeKey.ORGANISATION_NAME):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Name updated from ${item.oldValue} to ${item.newValue}`
          default:
            return `Unknown name action ${item.updateType}`
        }

      case (HistoryAttributeKey.ORGANISATION_INITIATIVE):
        switch (item.updateType) {
          case HistoryUpdateType.ADD:
            return `Initiative(s) added: ${item.newValue}`;
          case HistoryUpdateType.DELETE:
            return `Initiative(s) removed: ${item.oldValue}`;
          default:
            return `Unknown initiative action ${item.updateType}`
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBERSHIP_INTEREST):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if (item.newValue?.toUpperCase() === 'FALSE')
              return `Membership interest updated to 'no'`;
            else if (item.newValue?.toUpperCase() === 'TRUE')
              return `Membership interest updated to 'yes'`;
            else
              return `Membership interest updated to ${item.newValue}`;
          default:
            return `Unknown membership interest action ${item.updateType}`
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBER):
        switch (item.updateType) {
          case HistoryUpdateType.CREATE:
            return `Membership record created`;
          case HistoryUpdateType.DESTROY:
            return `Membership record removed`;
          default:
            return `Unknown membership record action ${item.updateType}`;
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBER_TYPE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member type updated from ${capitalizeFirstLetter(item.oldValue)} to ${capitalizeFirstLetter(item.newValue)}`;
          default:
            return `Unknown member type action ${item.updateType}`;
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBER_WILLING_TO_RECOMMEND_EUFIC):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if (item.newValue?.toUpperCase() === 'FALSE')
              return `Willing to recommend EUFIC set to 'no'`;
            else if (item.newValue?.toUpperCase() === 'TRUE')
              return `Willing to recommend EUFIC set to 'yes'`;
            else
              return `Willing to recommend EUFIC set to ${item.newValue}`;
          default:
            return `Unknown member willing to recommend EUFIC action ${item.updateType}`;
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBER_ENGAGEMENT_LEVEL):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member engagement level updated from ${capitalizeFirstLetter(item.oldValue)} to ${capitalizeFirstLetter(item.newValue)}`;
          default:
            return `Unknown member engagement level action ${item.updateType}`;
        }

      case (HistoryAttributeKey.ORGANISATION_MEMBER_CONTRACT_START_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member contract start date updated from ${item.oldValue} to ${item.newValue}`;
          default:
            return `Unknown member contract start date action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_CONTRACT_END_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member contract end date updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown member contract end date action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_TRIAL_START_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member trial start date updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown member trial start date action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_TRIAL_END_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Member trial end date updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown member trial end date action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_PRIMARY_CONTACT):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if ((item.oldValue !== null) && (item.newValue !== null))
              return `Member primary contact updated from ${item.oldValue} to ${item.newValue}`;
            else if ((item.oldValue === null) && (item.newValue !== null))
              return `Member primary contact set to ${item.newValue}`;
            else if ((item.oldValue !== null) && (item.newValue === null))
              return `Member primary contact removed (was ${item.oldValue})`;
            else
              return `Member primary contact untouched`;
          default:
            return `Unknown member primary contact action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_INVOICING_CONTACT):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if ((item.oldValue !== null) && (item.newValue !== null))
              return `Member invoicing contact updated from ${item.oldValue} to ${item.newValue}`;
            else if ((item.oldValue === null) && (item.newValue !== null))
              return `Member invoicing contact set to ${item.newValue}`;
            else if ((item.oldValue !== null) && (item.newValue === null))
              return `Member invoicing contact removed (was ${item.oldValue})`;
            else
              return `Member invoicing contact untouched`;
          default:
            return `Unknown member invoicing contact action ${item.updateType}`;
        }
      case (HistoryAttributeKey.ORGANISATION_MEMBER_CONTRACT_CONTACT):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            if ((item.oldValue !== null) && (item.newValue !== null))
              return `Member contract contact updated from ${item.oldValue} to ${item.newValue}`;
            else if ((item.oldValue === null) && (item.newValue !== null))
              return `Member contract contact set to ${item.newValue}`;
            else if ((item.oldValue !== null) && (item.newValue === null))
              return `Member contract contact removed (was ${item.oldValue})`;
            else
              return `Member contract contact untouched`;
          default:
            return `Unknown member contract contact action ${item.updateType}`;
        }
      default:
        return `Unknown item ${item.attributeKey} ${item.updateType}`
    }
  }

  handleProjectFields(item: HistoryItem) {
    switch (item.attributeKey) {
      case (HistoryAttributeKey.PROJECT_START_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Project start date updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown project start date action ${item.updateType}`;
        }
      case (HistoryAttributeKey.PROJECT_END_DATE):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Project end date updated from ${item.oldValue !== null ? item.oldValue : 'none'} to ${item.newValue !== null ? item.newValue : 'none'}`;
          default:
            return `Unknown project end date action ${item.updateType}`;
        }
      default:
        return `Unknown item ${item.attributeKey} ${item.updateType}`
    }
  }

  handleEventFields(item: HistoryItem) {
    switch (item.attributeKey) {
      case (HistoryAttributeKey.EVENT_NAME):
        switch (item.updateType) {
          case HistoryUpdateType.UPDATE:
            return `Name changed from ${item.oldValue} to ${item.newValue}`
          default:
            return `Unknown event action ${item.updateType}`
        }
      default:
        return `Unknown item ${item.attributeKey} ${item.updateType}`
    }
  }


}
